// Thread picker for AppAssistant — slim Select-like dropdown sitting in
// the panel header. Lists the user's recent ai-chat threads + a
// "+ New thread" footer. Selecting a thread calls
// `el.call('aiChatLoadThread', threadId)` to swap `root.assistantMessages`
// to that thread's history; "+ New thread" calls `aiChatCreateThread`.
//
// All data flows through the workspace `aiChat` SDK service
// (sdk/src/services/AiChatService.js → aiChat.threads.*). The picker
// itself owns no auth/network — handlers in
// workspace/functions/aiAssistantThreads.js do the IO.
//
// Reactivity: reads `root._aiThreads` (array) + `root._aiThreadId`
// (active thread id). Both are populated on first open by
// aiChatListThreads's onInit + stay in sync via the same handler.
//
// Frankability: `(el, s)` factories only, no destructure, no
// sibling-imports, all icons by bare name. Auto-extends nothing; it
// sits as a regular Flex with a button + an absolute menu.

// Active workspace id — same resolution order + priority as chat's
// `getActiveWorkspaceId` (workspace/packages/chat/functions/chatUtils.js):
// the onWorkspaceChange-mirrored `root.activeWorkspaceId` outranks the JWT
// claim, which can lag a federated re-mint indefinitely. Inlined (not
// imported) — component files call project functions via `el.call`, never
// cross-file imports; this is cheap enough to keep local.
const _pickerActiveWorkspaceId = (root) =>
  root?.activeWorkspaceId || root?.workspace?.id || root?.jwtClaims?.active_workspace_id || null

// Belt-and-suspenders filter vs a stale-JWT race — the server is the real
// scoping gate (AiChatController.listThreads → resolveAiChatScope,
// server@22ff64d4, 2026-07-14 WORKSPACE-scoping decision). Rows that
// predate the AiChatThread.workspace backfill normalize to
// `workspaceId: null` in aiAssistantThreads.js's `_normalizeThread` and
// pass through here unfiltered (a null isn't evidence of a cross-tenant
// row, just an unbackfilled one) — only an EXPLICIT mismatch is dropped.
const _scopedThreads = (root) => {
  const list = root?._aiThreads || []
  const wsId = _pickerActiveWorkspaceId(root)
  if (!wsId) return list
  return list.filter((t) => !t?.workspaceId || t.workspaceId === wsId)
}

const _activeLabel = (el, root) => {
  const id = root?._aiThreadId
  if (!id) return el.call('polyglot', 'workspace.simone.newChat')
  const t = _scopedThreads(root).find((x) => x?.id === id || x?._id === id)
  if (!t) return el.call('polyglot', 'workspace.threadPicker.thread')
  return t.title || t.subject || el.call('polyglot', 'workspace.threadPicker.thread')
}

export const AppAssistantThreadPicker = {
  position: 'relative',
  flow: 'x',
  align: 'center left',

  // Load threads on mount — keeps the dropdown populated by the time the
  // user clicks it. Root-state guard (NOT an element-local flag): threads
  // are WORKSPACE-scoped (2026-07-14 per Nika) and PHASE 1 of
  // onWorkspaceChange (loadPrivateData.js) wipes `root._aiThreads` +
  // `root._aiThreadsLoadAttempted` back to their schema-empty state on
  // every switch, but this drawer is gated by `hide` (never unmounted —
  // see app.js AssistantDrawer), so onInit only ever fires once per shell
  // lifetime. An `el._loadedThreads` instance flag would therefore
  // permanently suppress every future list call from this component,
  // including any the picker itself needs to issue post-switch.
  // aiChatListThreads is idempotent (bails on its own `_aiThreadsLoading`
  // in-flight guard), so bailing here only on the ROOT's loading/attempted
  // state — not an instance flag — is what lets a re-list actually reach
  // the network after a workspace switch.
  onInit: (el, s) => {
    if (typeof el?.context?.functions?.aiChatListThreads !== 'function') return
    if (s.root?._aiThreadsLoading || s.root?._aiThreadsLoadAttempted) return
    el.call('aiChatListThreads', s.root)
  },

  // Trigger: shows current thread label + chevron. Styling mirrors the
  // header's CloseBtn (lightweight chrome) so the picker reads as part
  // of the header rather than a heavy dropdown control.
  Trigger: {
    tag: 'button',
    type: 'button',
    flow: 'x',
    align: 'center',
    gap: 'V',
    padding: 'V Z',
    round: 'radiusControl',
    border: 'none',
    background: 'transparent',
    color: 'caption',
    fontSize: 'Z2',
    fontWeight: '500',
    cursor: 'pointer',
    maxWidth: 'F',
    ':hover': { background: 'hover', color: 'title' },
    title: (el) => el.call('polyglot', 'workspace.threadPicker.switch'),

    onClick: (e, el, s) => {
      e.preventDefault()
      e.stopPropagation()
      s.root.update({ _aiThreadMenuOpen: !s.root?._aiThreadMenuOpen })
    },

    Label: {
      tag: 'span',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => _activeLabel(el, s.root)
    },
    Chevron: { tag: 'span', fontSize: 'Y2', opacity: '.6', text: '▾' }
  },

  Menu: {
    hide: (el, s) => !s.root?._aiThreadMenuOpen,
    position: 'absolute',
    top: '110%',
    left: '0',
    zIndex: '20000',
    minWidth: 'G',
    maxHeight: 'I',
    overflow: 'auto',
    // Don't chain this overlay picker's edge-bounce to the surface behind (iPad P2).
    overscrollBehavior: 'contain',
    flow: 'y',
    padding: 'V',
    round: 'radiusControl',
    // Fill only — matches the house `MenuPanel` primitive exactly
    // (`theme: 'dialog'` + `shadow: 'overlay'` + `borderWidth: '0'`); the
    // hairline here was the habit.
    theme: 'dialog',
    borderWidth: '0',
    shadow: 'overlay',

    // Outside-press dismissal. This was an `onCreate` that stamped `el._close`
    // and called `document.addEventListener('mousedown', …)`, paired with an
    // `onRemove` that took it off again. The pair was correct, but it could
    // drift out of step with a later edit and `onCreate` firing twice would
    // have added a second listener while overwriting the only reference to the
    // first. Declared, DOMQL registers it once and disposes it (FA503).
    onDocumentMousedown: (ev, el, s) => {
      if (!el.node?.parentElement) return
      if (el.node.parentElement.contains(ev.target)) return
      s.root.update({ _aiThreadMenuOpen: false })
    },

    Items: {
      flow: 'y',
      gap: 'V',
      childrenAs: 'state',
      children: (el, s) => _scopedThreads(s.root),
      childExtends: {
        tag: 'button',
        type: 'button',
        flow: 'x',
        align: 'center space-between',
        gap: 'V',
        width: '100%',
        padding: 'Z A',
        round: 'radiusControl',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        fontSize: 'Z2',
        background: (el, s) => {
          const active = s.root?._aiThreadId === (s.id || s._id)
          return active ? 'overlay' : 'transparent'
        },
        color: 'title',
        ':hover': { background: 'hover' },

        Label: {
          tag: 'span',
          flex: '1',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          text: (el, s) =>
            el.call('polyglot', s.title || s.subject || 'workspace.assistant.threadFallback')
        },

        Check: {
          tag: 'span',
          color: 'green',
          text: (el, s) => {
            const active = s.root?._aiThreadId === (s.id || s._id)
            return active ? '✓' : ''
          }
        },

        onClick: (e, el, s) => {
          e.preventDefault()
          e.stopPropagation()
          s.root.update({ _aiThreadMenuOpen: false })
          el.call('aiChatLoadThread', s.root, s.id || s._id)
        }
      }
    },

    Empty: {
      hide: (el, s) => _scopedThreads(s.root).length > 0,
      tag: 'span',
      padding: 'Z A',
      fontSize: 'Z2',
      color: 'caption',
      text: '{{ workspace.threadPicker.empty | polyglot }}'
    },

    Divider: { height: 'hairline', background: 'line', margin: 'V 0' },

    NewBtn: {
      tag: 'button',
      type: 'button',
      width: '100%',
      textAlign: 'left',
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      padding: 'Z A',
      round: 'radiusControl',
      fontSize: 'Z2',
      color: 'caption',
      ':hover': { background: 'hover', color: 'title' },
      text: '{{ workspace.threadPicker.new | polyglot }}',
      onClick: (e, el, s) => {
        e.preventDefault()
        e.stopPropagation()
        s.root.update({ _aiThreadMenuOpen: false })
        el.call('aiChatCreateThread', s.root)
      }
    }
  }
}
